import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Box } from '@chakra-ui/react';
import { TabMenu, type TabItem } from '@themes/aegix';
import { ConfigurableTypeManager } from './ConfigurableTypeManager';
import {
  REUNIFICATION_TYPE_OPTIONS,
  type TypeCategory,
} from '../../types/ReunificationTypes';

const urlSubtab: Record<string, TypeCategory> = {
  'staff roles': 'staff-role-types',
  stations: 'station-types',
  zones: 'zone-types',
  signage: 'label-types',
};

export const getMatchedTab = (subtab: string | null): TypeCategory | null => {
  if (!subtab) return null;
  if (subtab in urlSubtab) return urlSubtab[subtab];

  let formatted = subtab.toLowerCase().replace(/\s+/g, '-');
  if (!formatted.endsWith('-types')) {
    formatted += '-types';
  }
  return formatted as TypeCategory;
};

export function ReunificationContent() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const subtab = getMatchedTab(searchParams.get('subtab'));
  const activeTab = subtab || 'staff-role-types';

  const localizedTabs = useMemo<TabItem[]>(
    () =>
      REUNIFICATION_TYPE_OPTIONS.map((opt) => ({
        value: opt.value,
        label: t(`reunification.types.${opt.value}`, opt.label),
        'data-e2e': `tab-${opt.value}`,
      })),
    [t],
  );

  const handleTabChange = (val: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('subtab', val);
      next.delete('id');
      next.delete('public_id');
      return next;
    });
  };

  return (
    <Box>
      <TabMenu
        tabs={localizedTabs}
        activeTab={activeTab}
        onChange={handleTabChange}
      />
      <ConfigurableTypeManager category={activeTab} />
    </Box>
  );
}

